import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FolderKanban, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@sd/hooks/use-mobile";
import ServiceDeskAPIService, {
  buildTrackerReportQueryParams,
  ptsOpenProjectListQueryKey,
  serviceDeskAgentAssignedProjectsQueryKey,
  serviceDeskTrackerReportQueryKeyWith,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";
import { projectFilterOptionsWithSelection } from "@sd/lib/ptsDropdownOptions";
import { isExternalSdRole } from "@sd/lib/sdRoles";
import { getServiceDeskUserId, useAuth } from "@sd/hooks/useAuth";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import {
  TRACKER_REPORT_ROWS,
  TRACKER_REPORT_STATUS_COLUMNS,
  type TrackerReportRowId,
  type TrackerReportStatusId,
} from "@sd/lib/trackerReportConstants";
import {
  buildTrackerDrillDownPath,
  isTrackerReportFromProjects,
  parseTrackerReportPageSearchParams,
  type TrackerDrillDownColumn,
  type TrackerReportPanel,
} from "@sd/lib/trackerReportDrillDown";
import {
  emptyTrackerReportFilters,
  hasActiveTrackerReportFilters,
  mergeTrackerReportFilters,
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";
import { ASSIGNED_SELECT_NONE } from "@sd/lib/adminTicketUtils";
import { CreatedDateFilterSelect } from "@sd/components/common/CreatedDateFilterSelect";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { persistTrackerReportReturnFilters } from "@sd/lib/ticketListDrillDownStorage";
import { SearchableFilterSelect } from "@sd/components/common/SearchableFilterSelect";
import { UserRound } from "lucide-react";
import {
  buildTrackerStatusChartData,
  buildTrackerTrackerChartData,
  downloadTrackerCsv,
  parseTrackerReportsPayload,
} from "@sd/lib/trackerReportApi";
import { TrackerStatusChart } from "@sd/components/reports/TrackerStatusChart";
import { TrackerTrackerChart } from "@sd/components/reports/TrackerTrackerChart";

function MatrixCell({
  value,
  className,
  onClick,
  staticLabel,
}: {
  value: number;
  className?: string;
  onClick?: () => void;
  /** Summary total row — bold text only, no drill-down. */
  staticLabel?: boolean;
}) {
  if (value <= 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  if (staticLabel) {
    return (
      <span className={cn("font-bold text-foreground", className)}>{value}</span>
    );
  }
  return (
    <button
      type="button"
      className={cn("text-primary hover:underline", className)}
      title="View filtered tickets"
      onClick={onClick}
    >
      {value}
    </button>
  );
}

export function TrackerReportDashboard({
  panel = "admin",
  disableDrillDown = false,
}: {
  panel?: TrackerReportPanel;
  disableDrillDown?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<TrackerReportFilters>(() =>
    mergeTrackerReportFilters(parseTrackerReportPageSearchParams(searchParams))
  );
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const { role } = useAuth();
  const isExternalUser = isExternalSdRole(role);
  const isAgentOnlyRole = role === "ServiceDesk_Agent";
  const scopedUserId =
    isExternalUser || isAgentOnlyRole ? getServiceDeskUserId() : null;
  const externalUserId = isExternalUser ? scopedUserId : null;
  const agentEmpId = isAgentOnlyRole ? scopedUserId : null;
  const { agentEmployees } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: !isExternalUser,
  });

  const { projectFilters, assigneeFilter, createdDateFilter } = filters;
  const hideFilters = isTrackerReportFromProjects(searchParams);

  const setProjectFilters = (projectFilters: string[]) => {
    setFilters(prev => ({ ...prev, projectFilters }));
  };
  const setAssigneeFilter = (assigneeFilter: string) => {
    setFilters(prev => ({ ...prev, assigneeFilter }));
  };
  const setCreatedDateFilter = (
    createdDateFilter: TrackerReportFilters["createdDateFilter"]
  ) => {
    setFilters(prev => ({ ...prev, createdDateFilter }));
  };

  const assigneeFilterOptions = useMemo(
    () => [
      { value: "all", label: "All Assignees" },
      { value: ASSIGNED_SELECT_NONE, label: "Unassigned" },
      ...agentEmployees.map(agent => ({ value: agent.id, label: agent.name })),
    ],
    [agentEmployees]
  );

  useEffect(() => {
    const restored = parseTrackerReportPageSearchParams(searchParams);
    if (!restored) return;
    setFilters(mergeTrackerReportFilters(restored));
  }, [searchParams]);

  useEffect(() => {
    persistTrackerReportReturnFilters(filters);
  }, [filters]);

  const clearFilters = () => {
    setFilters(emptyTrackerReportFilters());
  };

  const { data: assignedProjectsData, isPending: assignedProjectsLoading } =
    useQuery({
      queryKey: serviceDeskUserProjectQueryKey(externalUserId),
      queryFn: async () => {
        const response = await ServiceDeskAPIService.userProjects.list(
          externalUserId ?? undefined
        );
        return response.data;
      },
      enabled: Boolean(externalUserId),
      staleTime: 15_000,
    });

  const { data: agentAssignedProjectsData, isPending: agentAssignedProjectsLoading } =
    useQuery({
      queryKey: serviceDeskAgentAssignedProjectsQueryKey(agentEmpId),
      queryFn: async () => {
        const response = await ServiceDeskAPIService.agentAssignedProjects.list(
          agentEmpId ?? undefined
        );
        return response.data;
      },
      enabled: Boolean(agentEmpId),
      staleTime: 15_000,
    });

  const { data: ptsProjectsData, isPending: ptsProjectsLoading } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: !isExternalUser && !isAgentOnlyRole,
    staleTime: 5 * 60 * 1000,
  });

  const projectOptions = useMemo(() => {
    const source = isExternalUser
      ? assignedProjectsData
      : isAgentOnlyRole
        ? agentAssignedProjectsData
        : ptsProjectsData;
    return projectFilterOptionsWithSelection(source, projectFilters);
  }, [
    agentAssignedProjectsData,
    assignedProjectsData,
    isAgentOnlyRole,
    isExternalUser,
    projectFilters,
    ptsProjectsData,
  ]);

  const assignedProjectIds = useMemo(
    () =>
      isExternalUser || isAgentOnlyRole
        ? projectOptions.map(option => option.value)
        : null,
    [isAgentOnlyRole, isExternalUser, projectOptions]
  );

  const projectsLoading = isExternalUser
    ? assignedProjectsLoading
    : isAgentOnlyRole
      ? agentAssignedProjectsLoading
      : ptsProjectsLoading;

  const reportProjectFilters = useMemo(() => {
    if (!assignedProjectIds) return projectFilters;
    return projectFilters.filter(id => assignedProjectIds.includes(id));
  }, [assignedProjectIds, projectFilters]);

  const reportFilters = useMemo(
    () => ({
      ...filters,
      projectFilters: reportProjectFilters,
      assigneeFilter: isExternalUser ? "all" : filters.assigneeFilter,
    }),
    [filters, isExternalUser, reportProjectFilters]
  );

  const reportParams = useMemo(
    () => buildTrackerReportQueryParams(reportFilters),
    [reportFilters]
  );

  const hasProjectSelected = reportProjectFilters.length > 0;
  const hasActiveFilters = hasActiveTrackerReportFilters(reportFilters);

  const goToFilteredTickets = (
    rowId: TrackerReportRowId | undefined,
    column: TrackerDrillDownColumn,
    statusId?: TrackerReportStatusId
  ) => {
    if (disableDrillDown) return;
    if (reportProjectFilters.length === 0) return;
    navigate(
      buildTrackerDrillDownPath(
        {
          projectFilters: reportProjectFilters,
          assigneeFilter,
          createdDateFilter,
          rowId,
          column,
          statusId,
        },
        panel
      )
    );
  };

  const {
    data: apiPayload,
    isPending,
    isError,
  } = useQuery({
    queryKey: serviceDeskTrackerReportQueryKeyWith(reportFilters),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.reports.tracker(
        reportParams
      );
      if (response?.data === undefined) {
        throw new Error("Failed to load tracker report");
      }
      return response.data;
    },
    enabled: hasProjectSelected,
    staleTime: 60_000,
  });

  const matrix = useMemo(
    () => parseTrackerReportsPayload(apiPayload),
    [apiPayload]
  );

  const chartData = useMemo(
    () => buildTrackerStatusChartData(matrix),
    [matrix]
  );

  const trackerChartData = useMemo(
    () => buildTrackerTrackerChartData(matrix),
    [matrix]
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-col overflow-y-auto bg-background p-3 sm:p-4 md:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-lg font-bold text-primary sm:text-2xl md:text-3xl">
            Reports
            <span className="mx-1.5">»</span>
          </p>
          <h1 className="mt-1 text-lg font-bold text-foreground sm:text-xl md:text-2xl">
            Tracker
          </h1>
        </div>
        {hideFilters ? null : (
          <div className="flex w-full shrink-0 sm:w-auto sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full gap-1.5 px-3 text-xs sm:w-auto sm:text-sm"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              title="Clear all filters"
              aria-label="Clear all filters"
            >
              <RefreshCw className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Clear All Filters</span>
            </Button>
          </div>
        )}
      </div>

      {hideFilters ? null : (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
          <div className="w-full min-w-0 sm:max-w-md xl:w-[280px]">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <FolderKanban className="h-3.5 w-3.5 text-primary" />
              Project
            </label>
            <SearchableMultiFilterSelect
              selectId="tracker-project"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              values={projectFilters}
              onValuesChange={setProjectFilters}
              options={projectOptions}
              placeholder="Select projects"
              searchPlaceholder="Search projects…"
              disabled={projectsLoading || projectOptions.length === 0}
              triggerClassName="h-9 w-full min-w-0"
            />
          </div>

          {isExternalUser ? null : (
          <div className="w-full min-w-0 sm:max-w-[200px]">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <UserRound className="h-3.5 w-3.5 text-primary" />
              Assignee
            </label>
            <SearchableFilterSelect
              selectId="tracker-assignee"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              value={assigneeFilter}
              onValueChange={setAssigneeFilter}
              options={assigneeFilterOptions}
              placeholder="All Assignees"
              searchPlaceholder="Search assignees…"
              triggerClassName="h-9 w-full min-w-0"
            />
          </div>
          )}

          <div className="w-full min-w-0 sm:w-auto">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Created date
            </label>
            <CreatedDateFilterSelect
              value={createdDateFilter}
              onChange={setCreatedDateFilter}
              responsive
            />
          </div>
        </div>
      )}

      {!hideFilters && projectsLoading ? (
        <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-border bg-card py-16 shadow-sm">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !hideFilters && projectOptions.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-sm sm:p-6">
          No projects available. Create or open a project in PTS first.
        </div>
      ) : !hasProjectSelected ? (
        hideFilters ? (
          <div className="overflow-hidden rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-sm sm:p-6">
            No project selected.
          </div>
        ) : (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-dashed border-border bg-card p-5 text-center shadow-sm sm:min-h-[240px] sm:p-8">
            <FolderKanban className="h-10 w-10 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">
              Select one or more projects to view the report
            </p>
            <p className="max-w-sm text-xs text-muted-foreground">
              Choose projects from the dropdown above. The tracker table and
              charts will load after you select at least one.
            </p>
          </div>
        )
      ) : isPending ? (
        <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-border bg-card py-16 shadow-sm">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="overflow-hidden rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive shadow-sm">
          Could not load tracker report. Please try again.
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-6">
          <div className="w-full min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            {isMobile ? (
              <p className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
                Swipe horizontally to view all status columns
              </p>
            ) : null}
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full min-w-[920px] border-collapse text-xs leading-snug sm:text-sm">
                <thead>
                  <tr className="bg-muted text-sm font-bold sm:text-base">
                    <th className="sticky left-0 z-10 min-w-[128px] border border-border bg-muted px-2 py-2.5 text-left text-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] sm:min-w-[140px]">
                      Tracker / Status
                    </th>
                    {TRACKER_REPORT_STATUS_COLUMNS.map(col => (
                      <th
                        key={col.id}
                        className="min-w-[72px] whitespace-nowrap border border-border px-2 py-2.5 text-center text-foreground sm:min-w-[84px]"
                      >
                        {col.label}
                      </th>
                    ))}
                    <th className="min-w-[56px] whitespace-nowrap border border-border px-2 py-2.5 text-center text-foreground">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TRACKER_REPORT_ROWS.map(row => (
                    <tr key={row.id} className="bg-card">
                      <td className="sticky left-0 z-[1] min-w-[128px] border border-border bg-card px-2 py-1.5 text-sm font-bold leading-tight text-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] sm:min-w-[140px]">
                        {row.label}
                      </td>
                      {TRACKER_REPORT_STATUS_COLUMNS.map(col => (
                        <td
                          key={col.id}
                          className="border border-border px-2 py-1.5 text-center"
                        >
                          <MatrixCell
                            value={matrix.cells[row.id][col.id]}
                            staticLabel={disableDrillDown}
                            onClick={() =>
                              goToFilteredTickets(row.id, "status", col.id)
                            }
                          />
                        </td>
                      ))}
                      <td className="border border-border px-2 py-1.5 text-center">
                        <MatrixCell
                          value={matrix.rowTotal[row.id]}
                          staticLabel={disableDrillDown}
                          onClick={() => goToFilteredTickets(row.id, "total")}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/70">
                    <td className="sticky left-0 z-[1] border border-border bg-muted/70 px-2 py-1.5 text-sm font-bold text-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                      Total
                    </td>
                    {TRACKER_REPORT_STATUS_COLUMNS.map(col => (
                      <td
                        key={col.id}
                        className="border border-border px-2 py-1.5 text-center"
                      >
                        <MatrixCell
                          staticLabel
                          value={matrix.colTotal[col.id]}
                        />
                      </td>
                    ))}
                    <td className="border border-border px-2 py-1.5 text-center">
                      <MatrixCell staticLabel value={matrix.grandTotal} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-1 border-t border-border px-3 py-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <span>Issues by Tracker</span>
              <button
                type="button"
                className="text-left text-primary hover:underline sm:text-right"
                onClick={() => downloadTrackerCsv(matrix)}
              >
                Also available in: CSV
              </button>
            </div>
          </div>

          <TrackerTrackerChart data={trackerChartData} />
          <TrackerStatusChart data={chartData} />
        </div>
      )}
    </div>
  );
}
